import React, { useState, useEffect, useRef } from 'react';
import useStyles from './sharedStyles';
import { FaQuestionCircle, FaTimes } from 'react-icons/fa';
import { Tooltip, Button } from '@material-ui/core';
import { genericTokenIcon } from './config';

// Define CSS for animations
const fadeInAnimation = {
  '@keyframes fadeIn': {
    from: { opacity: 0, transform: 'translateY(-20px)' },
    to: { opacity: 1, transform: 'translateY(0)' }
  },
  animation: 'fadeIn 0.3s ease-out'
};

const fadeOutAnimation = {
  '@keyframes fadeOut': {
    from: { opacity: 1, transform: 'translateY(0)' },
    to: { opacity: 0, transform: 'translateY(-20px)' }
  },
  animation: 'fadeOut 0.3s ease-out'
};

/**
 * Bottom bar used only in BubblesView. Extracted so that the bubble animation
 * can re-render independently of this toolbar.
 */
const BubblesBottomBar = React.memo(function BubblesBottomBar({
  isMobile,
  handleMenuClick,
  handleIntervalSelection,
  rangeSelected,
  bubbleDensity,
  setBubbleDensity,
  tokenCount,
  classes,
  sizeMode,
  priceSortMode,
  setPriceSortMode,
  hasAdBanner = false,
}) {
  const bottomOffset = hasAdBanner ? 60 : 0;
  return (
    <div
      id="bubblesBottomBar"
      style={{
        position: 'fixed',
        left: 0,
        bottom: bottomOffset,
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        backgroundColor: 'black',
        zIndex: 1100,
      }}
    >
      {/* Time range buttons using same structure as in App.js, with a bit of left padding */}
      <div className={`${classes.toolbarContainer}`} style={{ paddingLeft: '8px' }}>
        <div className={classes.advancedToolbar}>
          {/* Range Selector */}
          <div className={classes.toolbarSection}>
            <div className={classes.intervalToolbar}>
              {/* Time range buttons */}
              {handleIntervalSelection && ['30M', '6H', '12H', '24H'].map((interval) => (
                <button
                  key={interval}
                  className={`${classes.intervalButton} ${rangeSelected === interval ? classes.selected : ''}`}
                  onClick={() => handleIntervalSelection(interval)}
                >
                  {interval}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Centered price sort-mode toggle when in price mode */}
      <div style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
        {(sizeMode === 'price' || sizeMode === 'price_update') && (
          <div style={{
            display: 'flex',
            borderRadius: '16px',
            overflow: 'hidden',
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            border: '1px solid #444',
          }}>
            {/* Gainers */}
            <button
              onClick={() => setPriceSortMode('gainers')}
              style={{
                padding: '4px 10px',
                border: 'none',
                cursor: 'pointer',
                backgroundColor: priceSortMode === 'gainers' ? '#4CAF50' : 'transparent',
                color: 'white',
                fontSize: '11px',
                fontWeight: priceSortMode === 'gainers' ? 'bold' : 'normal',
                borderRight: '1px solid #444',
              }}
            >
              Gainers
            </button>
            {/* Volatility */}
            <button
              onClick={() => setPriceSortMode('volatility')}
              style={{
                padding: '4px 10px',
                border: 'none',
                cursor: 'pointer',
                backgroundColor: priceSortMode === 'volatility' ? '#2196F3' : 'transparent',
                color: 'white',
                fontSize: '11px',
                fontWeight: priceSortMode === 'volatility' ? 'bold' : 'normal',
                borderRight: '1px solid #444',
              }}
            >
              Volatility
            </button>
            {/* Losers */}
            <button
              onClick={() => setPriceSortMode('losers')}
              style={{
                padding: '4px 10px',
                border: 'none',
                cursor: 'pointer',
                backgroundColor: priceSortMode === 'losers' ? '#F44336' : 'transparent',
                color: 'white',
                fontSize: '11px',
                fontWeight: priceSortMode === 'losers' ? 'bold' : 'normal',
              }}
            >
              Losers
            </button>
          </div>
        )}
      </div>

      {/* Tokens count on the right */}
      <div style={{ display: 'flex', alignItems: 'center', marginRight: '20px' }}>
        <span style={{ color: '#aaa', marginRight: '5px', fontSize: '14px' }}>Tokens:</span>
        <span style={{ color: 'white', fontWeight: 'bold', fontSize: '14px' }}>
          {tokenCount}
        </span>
      </div>
    </div>
  );
});

/**
 * BubblesView - Displays tokens in a bubble layout with volume-based sizing
 * This component is designed to be used as a mode in the token view
 */
const BubblesView = ({ 
  tokenList, 
  graphData, 
  dimensions, 
  handleNodeClick, 
  dataSource,
  handleMenuClick, // For hamburger menu
  rangeSelected = '12H', // Default range selection
  handleIntervalSelection, // For time range buttons
  isDataReady = true, // Whether data is ready
  hasPriceHistoryData = false, // Whether price history data is available
  isMobile = false, // Mobile detection
  hasAdBanner = false,
}) => {
  const classes = useStyles();
  const containerRef = useRef(null);
  const [showHelp, setShowHelp] = useState(false);
  const [bubbles, setBubbles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [bubblesVisible, setBubblesVisible] = useState(false); // Track if bubbles should be visible yet
  const [sizeMode, setSizeMode] = useState('price');
  // Controls how we choose tokens in price mode:
  // 'gainers'    = top positive movers first, then biggest losers if needed.
  // 'volatility' = largest absolute movers (up or down),
  // 'losers'     = top negative movers first, then biggest gainers if needed.
  const [priceSortMode, setPriceSortMode] = useState('volatility');
  // Default to 'price' instead of 'volume'
  const [bubbleDensity, setBubbleDensity] = useState(1.0); // User-controlled density multiplier
  const frameCountRef = useRef(0); // Counter to track animation frames
  const [showInfoPanel, setShowInfoPanel] = useState(false);
  const animationRef = useRef();
  const bubblesRef = useRef([]);
  const hasAdBannerRef = useRef(hasAdBanner);
  const failedIconUrlsRef = useRef(new Set());

  const getBubbleIconSrc = (bubble) => {
    const symbol = bubble && bubble.symbol ? String(bubble.symbol).toLowerCase() : 'generic';
    const candidates = [
      bubble && bubble.icon && String(bubble.icon).includes('.svg')
        ? String(bubble.icon).replace('.svg', '.png')
        : (bubble && bubble.icon ? String(bubble.icon) : ''),
      `https://coinicons-api.vercel.app/api/icon/${symbol}`,
      `https://cryptoicons.org/api/icon/${symbol}/200`,
      `https://cdn.jsdelivr.net/gh/atomiclabs/cryptocurrency-icons@master/128/color/${symbol}.png`,
    ].filter(Boolean);

    for (const candidate of candidates) {
      if (!failedIconUrlsRef.current.has(candidate)) return candidate;
    }

    return genericTokenIcon;
  };

  // Keep a ref in sync for use inside the animation loop without re-creating
  // the callback on every prop change.
  useEffect(() => {
    hasAdBannerRef.current = hasAdBanner;
  }, [hasAdBanner]);
  
  // Initialize physics properties for straight-line motion
const initializePhysics = (initialBubbles) => {
  // Reduce speed range for smoother, slower animation
  const MIN_SPEED = 0.15;
  const MAX_SPEED = 0.4;

  return initialBubbles.map((bubble) => {
    const angle = Math.random() * Math.PI * 2;

    // Calculate speed based on size and rank
    // Larger bubbles should move more slowly
    const sizeScale = Math.max(8, Math.min(320, bubble.size));
    const sizeRatio = 8 / sizeScale; // Size ratio (smaller bubbles = higher ratio)
    
    // Also factor in rank for a bit more variation
    const rankFactor = bubble.rank ? Math.max(0.7, 1.3 - bubble.rank / 100) : 1.0;
    
    // Combine size and rank factors
    const combinedFactor = sizeRatio * rankFactor;
    
    // Base speed with randomization
    const baseSpeed = MIN_SPEED + Math.random() * (MAX_SPEED - MIN_SPEED);
    const speed = baseSpeed * combinedFactor;

    const vx = Math.cos(angle) * speed;
    const vy = Math.sin(angle) * speed;

    return {
      ...bubble,
      vx,
      vy,
      restitution: 1.0,     // perfectly elastic collisions
      mass: Math.max(1, bubble.size * bubble.size / 64),  // mass proportional to area (size^2)
    };
  });
};

 // Animation function for physics simulation
const animate = () => {
  if (!containerRef.current || bubblesRef.current.length === 0) return;
  
  // Use the actual rendered container size as the primary bounds so bubbles
  // never extend beyond the visible area, and clamp by the viewport minus any
  // bottom ad banner height.
  const rect = containerRef.current.getBoundingClientRect();
  const rawWidth = rect.width || window.innerWidth;
  const rawHeight = rect.height || window.innerHeight;

  const adBanner = document.getElementById('adBannerContainer');
  const adBannerHeight = adBanner ? adBanner.clientHeight : 0;

  const containerWidth = rawWidth;
  const containerHeight = Math.min(rawHeight, window.innerHeight - adBannerHeight);
  
  // Occasionally log the container dimensions being used (every ~200 frames)
  if (Math.random() < 0.005) {
    console.log(`Animation using container dimensions: ${containerWidth}x${containerHeight}`);
  }
    
  // Function to ensure all bubbles are within bounds
  const ensureAllBubblesInBounds = (bubbles) => {
    const safetyMargin = 10; // Increased safety margin for initial positioning
    const baseBottomMargin = 80; // Space for bottom toolbar and UI
    const dynamicBottomMargin = hasAdBannerRef.current ? baseBottomMargin + 60 : baseBottomMargin;
    const bottomMargin = dynamicBottomMargin;
    
    bubbles.forEach(b => {
      const diameter = b.size * 2;
      
      // Left/right bounds checking
      if (b.x < safetyMargin) {
        b.x = safetyMargin;
        b.vx = Math.abs(b.vx) * 1.05;
      } else if (b.x + diameter > containerWidth - safetyMargin) {
        b.x = containerWidth - diameter - safetyMargin;
        b.vx = -Math.abs(b.vx) * 1.05;
      }
      
      // Top/bottom bounds checking
      if (b.y < safetyMargin) {
        b.y = safetyMargin;
        b.vy = Math.abs(b.vy) * 1.05;
      } else if (b.y + diameter > containerHeight - bottomMargin) {
        // Use extra margin at the bottom
        b.y = containerHeight - diameter - bottomMargin;
        b.vy = -Math.abs(b.vy) * 1.5;
        
        // Apply a slight horizontal force for more interesting movement
        if (Math.abs(b.vx) < 0.5) {
          b.vx += (Math.random() - 0.5) * 0.5;
        }
      }
    });
  };
  
  // Increment frame counter
  frameCountRef.current += 1;
  
  // After 60 animation frames, make bubbles visible
  // This ensures they've moved significantly from their initial positions
  if (frameCountRef.current >= 60 && !bubblesVisible) {
    // Check if any bubbles are beyond the bounds and correct them
    ensureAllBubblesInBounds(bubblesRef.current);
    // Now make bubbles visible
    setBubblesVisible(true);
  }

  // Reduced speed limits for smoother animation
  const MIN_SPEED = 0.15;
  const MAX_SPEED = 0.6;

  const bubbles = bubblesRef.current;

  // Helper to keep bubbles inside and bounce off walls with extra safety margin
  const clampToWalls = (b) => {
    const diameter = b.size * 2;
    const safetyMargin = 10; // Increased safety margin to ensure bubbles stay fully visible
    const baseBottomMargin = 80; // Space for bottom toolbar and UI
    const dynamicBottomMargin = hasAdBannerRef.current ? baseBottomMargin + 60 : baseBottomMargin;
    const bottomMargin = dynamicBottomMargin;
    
    // Left / right
    if (b.x < safetyMargin) {
      // Bounce off left wall with extra margin
      b.x = safetyMargin;
      b.vx = Math.abs(b.vx) * 1.05; // Stronger bounce
    } else if (b.x + diameter > containerWidth - safetyMargin) {
      // Bounce off right wall with extra margin
      b.x = containerWidth - diameter - safetyMargin;
      b.vx = -Math.abs(b.vx) * 1.05; // Stronger bounce
    }

    // Top / bottom
    if (b.y < safetyMargin) {
      // Bounce off top wall with extra margin
      b.y = safetyMargin;
      b.vy = Math.abs(b.vy) * 1.05; // Stronger bounce
    } else if (b.y + diameter > containerHeight - bottomMargin) {
      // Ensure bubbles don't go below bottom edge with extra margin
      b.y = containerHeight - diameter - bottomMargin;
      // Give a much stronger upward bounce to ensure visibility
      b.vy = -Math.abs(b.vy) * 1.5; // Much stronger upward bounce at bottom
      
      // Apply a slight horizontal force for more interesting movement
      if (Math.abs(b.vx) < 0.5) {
        b.vx += (Math.random() - 0.5) * 0.5;
      }
    }
  };

  const clampSpeed = (b) => {
    let { vx, vy } = b;
    let speed = Math.sqrt(vx * vx + vy * vy) || 0;

    if (speed < MIN_SPEED) {
      const factor = MIN_SPEED / (speed || 1);
      vx *= factor;
      vy *= factor;
    } else if (speed > MAX_SPEED) {
      const factor = MAX_SPEED / speed;
      vx *= factor;
      vy *= factor;
    }

    b.vx = vx;
    b.vy = vy;
  };

  // 1) Integrate motion and handle wall bounces
  for (let i = 0; i < bubbles.length; i++) {
    const b = bubbles[i];
    b.x += b.vx;
    b.y += b.vy;

    clampToWalls(b);
  }

  // 2) Resolve bubble–bubble collisions
  for (let i = 0; i < bubbles.length; i++) {
    for (let j = i + 1; j < bubbles.length; j++) {
      const a = bubbles[i];
      const b = bubbles[j];

      const ax = a.x + a.size;
      const ay = a.y + a.size;
      const bx = b.x + b.size;
      const by = b.y + b.size;

      const dx = bx - ax;
      const dy = by - ay;
      const distSq = dx * dx + dy * dy;

      const minDist = a.size + b.size;
      const minDistSq = minDist * minDist;

      if (distSq > 0 && distSq < minDistSq) {
        const dist = Math.sqrt(distSq);
        const nx = dx / dist;
        const ny = dy / dist;

        // Separate the bubbles so they no longer overlap
        const overlap = minDist - dist;
        const correction = overlap / 2;
        a.x -= nx * correction;
        a.y -= ny * correction;
        b.x += nx * correction;
        b.y += ny * correction;

        // Reflect velocities along the collision normal
        const vaDotN = a.vx * nx + a.vy * ny;
        const vbDotN = b.vx * nx + b.vy * ny;

        // Tangential components stay the same
        const vaTx = a.vx - vaDotN * nx;
        const vaTy = a.vy - vaDotN * ny;
        const vbTx = b.vx - vbDotN * nx;
        const vbTy = b.vy - vbDotN * ny;

        const e = 0.9; // slightly inelastic for more natural behavior

        // Calculate velocities using conservation of momentum and energy with different masses
        const massSum = a.mass + b.mass;
        const vaNNew = ((a.mass - e * b.mass) * vaDotN + (1 + e) * b.mass * vbDotN) / massSum;
        const vbNNew = ((b.mass - e * a.mass) * vbDotN + (1 + e) * a.mass * vaDotN) / massSum;

        a.vx = vaTx + vaNNew * nx;
        a.vy = vaTy + vaNNew * ny;
        b.vx = vbTx + vbNNew * nx;
        b.vy = vbTy + vbNNew * ny;

        // Tiny jitter so the paths aren’t perfectly symmetrical
        const jitter = 0.02;
        a.vx += (Math.random() - 0.5) * jitter;
        a.vy += (Math.random() - 0.5) * jitter;
        b.vx += (Math.random() - 0.5) * jitter;
        b.vy += (Math.random() - 0.5) * jitter;

        // Make sure we didn’t push them outside the walls
        clampToWalls(a);
        clampToWalls(b);
      }
    }
  }

  // 3) Normalise speeds and add final safety checks
  for (let i = 0; i < bubbles.length; i++) {
    const b = bubbles[i];
    
    // Apply speed normalization
    clampSpeed(b);
    
    // Final safety check for bottom edge
    const diameter = b.size * 2;
    const baseBottomMargin = 80;
    const dynamicBottomMargin = hasAdBannerRef.current ? baseBottomMargin + 60 : baseBottomMargin;
    if (b.y + diameter > containerHeight - dynamicBottomMargin) {
      // Force repositioning if somehow still too close to bottom
      b.y = containerHeight - diameter - dynamicBottomMargin;
      b.vy = -Math.abs(b.vy);
    }
  }

  // Only update state reference (not React state) during animation to avoid disruption
  bubblesRef.current = bubbles;
  
  // Schedule next animation frame
  animationRef.current = requestAnimationFrame(animate);
  
  // Update React state only occasionally (every 30 frames) to reflect current positions in UI
  // without disrupting the animation flow
  if (Math.random() < 0.03) { // ~3% chance per frame
    setBubbles([...bubbles]);
  }
};

  
  // Log when price history data status changes
  useEffect(() => {
    console.log('Price history data status:', { 
      hasPriceHistoryData, 
      isDataReady, 
      tokenListLength: tokenList?.length || 0,
      hasPriceChangeData: tokenList?.[0]?.price_change_percent !== undefined
    });
  }, [hasPriceHistoryData, isDataReady, tokenList]);

  // Start/stop animation
  useEffect(() => {
    console.log('Animation effect triggered', { bubbles: bubbles.length, loading });
    
    if (bubbles.length > 0 && !loading) {
      console.log('Starting animation loop with', bubbles.length, 'bubbles');
      // Always make sure the ref is up to date
      bubblesRef.current = bubbles;
      
      // Cancel any existing animation frame first to avoid duplicates
      if (animationRef.current) {
        console.log('Cancelling existing animation frame');
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
      
      // Short delay before starting animation to ensure DOM is ready
      // This is especially important after window resize
      setTimeout(() => {
        console.log('Starting animation after delay');
        animationRef.current = requestAnimationFrame(animate);
      }, 50);
    } else {
      console.log('Not starting animation:', { bubbleCount: bubbles.length, isLoading: loading });
    }
    
    // Cleanup function
    return () => {
      console.log('Cleaning up animation');
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
    };
  }, [bubbles.length, loading]);
  
  // Add window resize listener to update bubble count dynamically
  useEffect(() => {
    // Debounce timer to avoid excessive updates
    let resizeTimer;
    
    // Handle window resize to update bubble count
    const handleResize = () => {
      // Clear the previous timer
      clearTimeout(resizeTimer);
      
      // Set a new timer to delay the update
      resizeTimer = setTimeout(() => {
        // Always force recalculation after resize, using current window size
        console.log('Window resized - forcing complete recalculation');
        
        // First stop animation to prevent visual artifacts
        if (animationRef.current) {
          cancelAnimationFrame(animationRef.current);
          animationRef.current = null;
        }
        
        // Critical: Force complete reset of state
        setLoading(true);
        setBubblesVisible(false);
        frameCountRef.current = 0;
        
        // Clear all bubbles to force complete recalculation
        setBubbles([]);
        
        // Wait a short moment for state to update
        setTimeout(() => {
          // Toggle mode to force effect to run with fresh calculation
          // This is the key to making it redraw with completely new positions
          setSizeMode(prevMode => {
            // Handle both price and volume modes for recalculation
            const newMode = prevMode === 'price' ? 'price_update' : 
                          prevMode === 'price_update' ? 'price' : 
                          prevMode === 'volume' ? 'volume_update' : 'volume';
            console.log(`Switching mode from ${prevMode} to ${newMode} to trigger recalculation`);
            return newMode;
          });
          
          // Force containerRef dimensions update
          if (containerRef.current) {
            console.log('Container element dimensions:', { 
              width: containerRef.current.clientWidth,
              height: containerRef.current.clientHeight 
            });
          }
          
          // Add a safety check to ensure loading state gets cleared
          setTimeout(() => {
            if (!bubblesRef.current || bubblesRef.current.length === 0) {
              console.log('RECOVERY: Forcing loading state to false after 1s');
              setLoading(false);
            }
          }, 1000);
        }, 50);
      }, 250);
    };
    
    // Add resize event listener
    window.addEventListener('resize', handleResize);
    
    // Cleanup listener and timer
    return () => {
      clearTimeout(resizeTimer);
      window.removeEventListener('resize', handleResize);
    };
  }, []);  // Empty dependency array - we'll handle dimensions internally

  // Add debug logging for props
  useEffect(() => {
    // Log detailed information about the props
    const tokenListLength = tokenList ? tokenList.length : 0;
    const hasGraphData = !!graphData;
    const graphDataNodes = graphData ? graphData.nodes.length : 0;
    
    console.log('BubblesView props:', {
      tokenListLength,
      hasGraphData,
      graphDataNodes,
      dimensions
    });
    
    // More detailed logging of the actual data
    if (tokenListLength > 0) {
      console.log('Sample token from tokenList:', tokenList[0]);
    }
    
    if (graphDataNodes > 0) {
      console.log('Sample node from graphData:', graphData.nodes[0]);
    }
    
    // Log warning if no data
    if (tokenListLength === 0 || graphDataNodes === 0) {
      console.warn('BubblesView received empty data. Check data sources and API responses.');
    }
  }, [tokenList, graphData, sizeMode, hasPriceHistoryData, bubbleDensity]);

  // Calculate bubble layout based on selected mode (volume or price), window size,
  // and current user-selected bubble density. We rely on window.innerWidth/Height
  // for viewport size so this can safely run even if dimensions prop is stale.
  useEffect(() => {
    if (!tokenList || tokenList.length === 0 || !graphData || !hasPriceHistoryData) {
      console.log('BubblesView: Missing required props or price history data', { 
        tokenList: !!tokenList, 
        tokenListLength: tokenList?.length, 
        graphData: !!graphData,
        hasPriceHistoryData: hasPriceHistoryData
      });
      setLoading(false);
      return;
    }
    
    // Check if we have per-range price history data in the tokenList
    const hasPriceHistory = tokenList.some(token => 
      token.changes && token.changes[rangeSelected] &&
      token.changes[rangeSelected].percent !== undefined);
    
    console.log(`Token list ${hasPriceHistory ? 'includes' : 'does not include'} price history data`);
    
    // Log the current dimensions and mode we're working with (fall back to
    // window size if dimensions prop is not available)
    const debugWidth = dimensions?.width ?? window.innerWidth;
    const debugHeight = dimensions?.height ?? window.innerHeight;
    console.log(`Calculating bubble layout with dimensions: ${debugWidth}x${debugHeight}, mode: ${sizeMode}`);
    
    // Log window dimensions for comparison
    console.log(`Window dimensions: ${window.innerWidth}x${window.innerHeight}`);
    
    // Reset animation state
    setError(null);
    setLoading(true);
    frameCountRef.current = 0;
    setBubblesVisible(false);
    
    console.log(`Processing data for ${sizeMode} mode...`);

    try {
      let sortedTokenVolumes = [];
      
      // Always use window.innerWidth/Height directly to ensure we have current dimensions
      // This is critical after resize to prevent dimensions prop lag
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const viewportArea = viewportWidth * viewportHeight;
      
      console.log('Current viewport dimensions (from window):', { width: viewportWidth, height: viewportHeight });

      // Calculate base count - one bubble per 8000px² of screen space
      const baseCount = Math.floor(viewportArea / 8000);

      // Determine device capabilities
      const isMobileDevice = viewportWidth < 768 || viewportHeight < 600;

      // Set reasonable min/max based on device capabilities and screen size
      const minBubbles = isMobileDevice ? 20 : 30;
      const maxBubbles = isMobileDevice ? 40 : 1000;

      // Dynamic bubble count based on viewport size with reasonable limits
      const maxTokensBase = Math.max(minBubbles, Math.min(maxBubbles, baseCount));

      // User-controlled density multiplier (clamped)
      const densityMultiplier = Math.max(0.5, Math.min(2.0, bubbleDensity));

      // For price mode, start from a higher base (about 60% of the dynamic
      // count) so the viewport is well-filled, then apply the density
      // multiplier directly.
      const basePriceModeCount = Math.max(5, Math.round(maxTokensBase * 0.6));
      const effectiveMaxTokensToShow = (sizeMode === 'price' || sizeMode === 'price_update')
        ? Math.max(5, Math.round(basePriceModeCount * densityMultiplier))
        : Math.max(5, Math.round(maxTokensBase * densityMultiplier));

      console.log(`Using dynamic bubble count: ${effectiveMaxTokensToShow} bubbles (base=${maxTokensBase}, density=${densityMultiplier.toFixed(2)}, viewport: ${viewportWidth}×${viewportHeight}px, area: ${viewportArea}px²)`)
      
      // ===== VOLUME MODE =====
      if (sizeMode === 'volume' || sizeMode === 'volume_update') {
        console.log('VOLUME MODE: Sorting by trading volume');
        // Get nodes with volume data
        const nodesWithVolume = graphData.nodes.filter(node => {
          return node.icon && node.icon.length > 0 && 
                 node.volume && Array.isArray(node.volume) && node.volume.length > 0;
        });
        
        console.log(`Found ${nodesWithVolume.length} nodes with volume data out of ${graphData.nodes.length} total nodes`);
        const availableCount = nodesWithVolume.length;
        
        // Calculate total volume for each node by summing the volume array
        const nodeVolumes = nodesWithVolume.map(node => {
          const volumeSum = node.volume.reduce((sum, vol) => sum + (typeof vol === 'number' ? vol : 0), 0);
          
          // Find matching token for additional data
          const matchingToken = tokenList.find(t => t.id === node.id);
          const price = (matchingToken && matchingToken.price) || 0;
          
          return {
            node,
            matchingToken,
            totalVolume: Math.max(100, volumeSum), // Ensure minimum size
            price
          };
        });

        // Use effectiveMaxTokensToShow (already scaled by density and viewport)
        // and cap by how many tokens actually have volume data.
        const targetCount = Math.max(
          5,
          Math.min(availableCount, effectiveMaxTokensToShow)
        );
        
        console.log(`Volume mode: available=${availableCount}, targetCount=${targetCount}, effectiveMax=${effectiveMaxTokensToShow}, density=${densityMultiplier.toFixed(2)}`);

        // Sort by actual volume and take top targetCount tokens
        sortedTokenVolumes = nodeVolumes
          .sort((a, b) => b.totalVolume - a.totalVolume)
          .slice(0, targetCount);
        
        console.log(`Top volume tokens: ${sortedTokenVolumes.slice(0, 5).map(item => 
          `${item.node.label || item.node.symbol}: ${item.totalVolume}`).join(', ')}`);
      }
      
      // ===== PRICE MODE =====
      else if (sizeMode === 'price' || sizeMode === 'price_update') {
        console.log('PRICE MODE: Sorting by price change percentage');
        
        // First get tokens with price change data for the selected range
        const tokensWithPriceChange = tokenList.filter(token => {
          const change = token.changes && token.changes[rangeSelected];
          return token.price && token.price > 0 && change && change.percent !== undefined;
        });
          
        console.log(`Found ${tokensWithPriceChange.length} tokens with price change data out of ${tokenList.length} total tokens`);
        const availableCount = tokensWithPriceChange.length;
        
        // Find matching nodes for these tokens
        const tokenPriceChanges = tokensWithPriceChange.map(token => {
          // Try to find a matching node in graph data
          const matchingNode = graphData && graphData.nodes
            ? graphData.nodes.find(n => n.id === token.id)
            : null;

          // If there's no matching graph node, synthesize a minimal node
          const node = matchingNode || {
            id: token.id,
            label: token.name,
            symbol: token.symbol,
            icon: token.icon || token.icon2 || '',
            type: 'token'
          };

          // Skip tokens that still have no icon at all
          if (!node.icon || node.icon.length === 0) {
            return null;
          }

          const change = token.changes && token.changes[rangeSelected];
          const signedPercent = change ? Number(change.percent) || 0 : 0;
          // Use absolute value of price change percent for sizing, but keep
          // the signed value for labels so we match the /price 1H column.
          const priceChangeAbs = Math.abs(signedPercent);
          
          return {
            node,
            matchingToken: token,
            totalVolume: priceChangeAbs * 1000, // Direct scaling with no minimum threshold
            price: token.price,
            signedPercent,
            priceChangeAbs
          };
        }).filter(item => item !== null); // Remove null entries

        // Use effectiveMaxTokensToShow (already scaled by density and viewport)
        // and cap by how many tokens actually have price-change data.
        const targetCount = Math.max(
          5,
          Math.min(availableCount, effectiveMaxTokensToShow)
        );

        console.log(`Price mode: range=${rangeSelected}, available=${availableCount}, targetCount=${targetCount}, effectiveMax=${effectiveMaxTokensToShow}, density=${densityMultiplier.toFixed(2)}, priceSortMode=${priceSortMode}`);
        
        if (priceSortMode === 'gainers') {
          // Top gainers view: prioritize positive movers by signed percent,
          // then backfill with largest losers (most negative) if needed.
          const ups = tokenPriceChanges
            .filter(item => item.signedPercent > 0)
            .sort((a, b) => b.signedPercent - a.signedPercent);

          const downs = tokenPriceChanges
            .filter(item => item.signedPercent <= 0)
            .sort((a, b) => a.signedPercent - b.signedPercent); // more negative first

          const selected = ups.slice(0, targetCount);
          if (selected.length < targetCount) {
            const needed = targetCount - selected.length;
            selected.push(...downs.slice(0, needed));
          }

          sortedTokenVolumes = selected;
        } else if (priceSortMode === 'losers') {
          // Top losers view: prioritize negative movers by signed percent,
          // then backfill with largest gainers if needed.
          const downs = tokenPriceChanges
            .filter(item => item.signedPercent < 0)
            .sort((a, b) => a.signedPercent - b.signedPercent); // more negative first

          const ups = tokenPriceChanges
            .filter(item => item.signedPercent >= 0)
            .sort((a, b) => b.signedPercent - a.signedPercent);

          const selected = downs.slice(0, targetCount);
          if (selected.length < targetCount) {
            const needed = targetCount - selected.length;
            selected.push(...ups.slice(0, needed));
          }

          sortedTokenVolumes = selected;
        } else {
          // Volatility view (default): sort by absolute price change magnitude
          // regardless of direction, so the biggest movers dominate visually.
          sortedTokenVolumes = tokenPriceChanges
            .sort((a, b) => b.priceChangeAbs - a.priceChangeAbs)
            .slice(0, targetCount);
        }
        
        console.log(`Top price change tokens: ${sortedTokenVolumes.slice(0, 5).map(item => {
          const raw = typeof item.signedPercent === 'number' && isFinite(item.signedPercent)
            ? item.signedPercent
            : 0;
          const sign = raw >= 0 ? '+' : '-';
          const magnitude = Math.abs(raw).toFixed(2);
          return `${item.node.label || item.node.symbol}: ${sign}${magnitude}%`;
        }).join(', ')}`);
      }
      
      // If no tokens made it through filtering, bail out early to avoid NaN layouts
      if (!sortedTokenVolumes || sortedTokenVolumes.length === 0) {
        console.warn('BubblesView: No tokens available after filtering for size mode', sizeMode);
        setBubbles([]);
        setBubblesVisible(false);
        return;
      }

      const displayCount = Math.min(effectiveMaxTokensToShow, sortedTokenVolumes.length);
      console.log(`Displaying top ${displayCount} nodes by ${sizeMode} out of ${sortedTokenVolumes.length} filtered nodes`);

      // Find min, max, and median volumes for proper scaling
      const volumes = sortedTokenVolumes.map(item => item.totalVolume);
      const sortedVolumes = [...volumes].sort((a, b) => a - b);
      
      // Get min and max for direct scaling
      const minVolume = sortedVolumes[0];
      const maxVolume = sortedVolumes[sortedVolumes.length - 1];
      const volumeRange = maxVolume - minVolume;
      
      console.log(`Volume range: min=${minVolume}, max=${maxVolume}, range=${volumeRange}`);
      
      // Also calculate median for fallback and reference
      const medianIndex = Math.floor(sortedVolumes.length / 2);
      const medianVolume = sortedVolumes.length % 2 === 0
        ? (sortedVolumes[medianIndex - 1] + sortedVolumes[medianIndex]) / 2
        : sortedVolumes[medianIndex];
        
      // Bubble size parameters: keep small bubbles readable but allow top movers to be dominant.
      // In price mode, use a larger minimum and maximum, but not as extreme, so labels are legible
      // and top movers are clearly bigger without filling the entire screen.
      const isPriceMode = (sizeMode === 'price' || sizeMode === 'price_update');
      const minRadius = isPriceMode ? 40 : 22;    // Floor ~80px diameter in price mode
      const maxRadius = isPriceMode ? 140 : 110;  // Still allows large bubbles, but about half previous
      const defaultBubbleRadius = 40; // Reference size
// Use fewer columns to give adequate space for larger bubbles
// Use about 1/2 as many columns as we would normally
const gridCols = Math.ceil(Math.sqrt(sortedTokenVolumes.length * 0.5));
const gridRows = Math.ceil(sortedTokenVolumes.length / gridCols);

// Reasonable margin to accommodate the largest bubbles
const margin = 150; // Reduced margin for the smaller bubbles

// Log the grid layout for debugging
console.log(`Grid layout: ${gridCols}x${gridRows} with ${margin}px margin for ${sortedTokenVolumes.length} bubbles`);
const usableWidth = dimensions.width - margin * 2;
const usableHeight = dimensions.height - margin * 2;

// Cell sizes inside the usable area
const cellWidth = usableWidth / gridCols;
const cellHeight = usableHeight / gridRows;

// Create bubbles with positions and sizes - use the sorted tokens
let newBubbles = sortedTokenVolumes.map((item, index) => {
  const rank = index + 1;
  // Declare normalizedVolumeValue at the beginning of the function scope
  let normalizedVolumeValue = 0;

  // For price mode, use direct price change percentage for sizing
  let scaledRadius;
  
  if (sizeMode === 'price' || sizeMode === 'price_update') {
    // Get the price change directly from the data
    const priceChangePercent = item.priceChange || 0;
    
    // Use a super-linear scaling so large movers are clearly bigger, while still
    // clamping extreme outliers
    const maxPriceChange = 12; // Visible scaling up to ~12%
    const clampedChange = Math.min(priceChangePercent, maxPriceChange);
    const linearRatio = clampedChange / maxPriceChange;
    // Exponent > 1 to give extra boost to higher movers
    const normalizedChange = Math.pow(linearRatio, 1.15);
    
    // Store normalized value (0-100 scale) for reference - used for logging later
    normalizedVolumeValue = Math.round(normalizedChange * 100);
    
    // Mapping to radius range with the flattened normalized value
    scaledRadius = minRadius + (normalizedChange * (maxRadius - minRadius));
    
    // No additional adjustments for price change mode
  } else {
    // Original volume-based scaling
    // Calculate normalized volume (0-1 scale) based on min-max values
    let normalizedValue;
    if (volumeRange === 0) {
      normalizedValue = 0.5;
    } else {
      normalizedValue = (item.totalVolume - minVolume) / volumeRange;
    }
    
    // Store normalized value (0-100 scale) for reference
    normalizedVolumeValue = Math.round(normalizedValue * 100);
    
    // Apply the original scaling calculations
    const scalePower = 1.2;
    const scaledNormalizedValue = Math.pow(normalizedValue, scalePower);
    scaledRadius = minRadius + (scaledNormalizedValue * (maxRadius - minRadius));
    scaledRadius = minRadius + ((scaledRadius - minRadius) * 0.8);
    
    // Apply rank-based adjustments for volume mode
    if (rank === 1) {
      scaledRadius = maxRadius;
    } else if (rank <= sortedTokenVolumes.length * 0.1) {
      const topPercentile = rank / (sortedTokenVolumes.length * 0.1);
      const minTopSize = maxRadius * 0.75;
      scaledRadius = maxRadius - (topPercentile * (maxRadius - minTopSize));
    } else if (rank >= sortedTokenVolumes.length * 0.9) {
      const bottomPercentile = (rank - sortedTokenVolumes.length * 0.9) / (sortedTokenVolumes.length * 0.1);
      const maxBottomSize = minRadius * 4;
      scaledRadius = maxBottomSize - (bottomPercentile * (maxBottomSize - minRadius));
    }
  }
  
  if (sizeMode === 'price' || sizeMode === 'price_update') {
    // Log price change information using a safe numeric value
    const rawPc = typeof item.signedPercent === 'number' && isFinite(item.signedPercent)
      ? item.signedPercent
      : (typeof item.priceChange === 'number' && isFinite(item.priceChange)
          ? item.priceChange
          : 0);
    console.log(`Token ${item.node.symbol || item.node.label}: price_change=${rawPc.toFixed(2)}%, scaled_radius=${Math.round(scaledRadius)}px`);
  } else {
    // Log volume information
    const logNormalizedVolume = sizeMode === 'price' ? 0 : Math.round(((item.totalVolume - minVolume) / volumeRange) * 100);
    console.log(`Token ${item.node.symbol || item.node.label}: volume=${item.totalVolume}, normalized=${logNormalizedVolume}, radius=${Math.round(scaledRadius)}px`);
  }
  
  // Format volume for display - do this early so it's available for all return statements
  const formattedVolume = item.totalVolume >= 1000000
    ? `${(item.totalVolume / 1000000).toFixed(1)}M`
    : item.totalVolume >= 1000
      ? `${(item.totalVolume / 1000).toFixed(1)}K`
      : item.totalVolume.toFixed(0);

  // Calculate a stable percentage change based on the current mode
  // For volume mode: Use volume relative to median
  // For price mode: Use actual price change or price relative to median
  
  // Determine if the token is "up" based on the selected mode or API data
  let isPriceUp;
  let percentageChange;
  let valueRatio;
  
  // First check if this token has price history data from API
  if (item.matchingToken && 
      item.matchingToken.price_change_percent !== undefined &&
      item.matchingToken.price_change_direction !== undefined) {
    
    // Use data directly from the API
    isPriceUp = item.matchingToken.price_change_direction === 'up';
    percentageChange = Math.abs(item.matchingToken.price_change_percent).toFixed(2);
    
    console.log(`Token ${item.matchingToken.name || item.matchingToken.symbol}: Using API price change data: ${isPriceUp ? '+' : '-'}${percentageChange}%`);
  }
  // Fall back to calculated values if no price history data
  else if (sizeMode === 'price' || sizeMode === 'price_update') {
    // For price mode: Check if the price is above median price
    isPriceUp = item.price > (item.matchingToken?.priceMedian || medianVolume/1000);
    
    // If token has 24h price change data, use that directly
    if (item.matchingToken?.priceChange24h) {
      percentageChange = Math.abs(item.matchingToken.priceChange24h).toFixed(2);
      // The isPriceUp is already determined by the actual price change sign
      isPriceUp = item.matchingToken.priceChange24h > 0;
    } else {
      // Calculate price relative to median if no change data
      valueRatio = item.price / (item.matchingToken?.priceMedian || medianVolume/1000);
      
      if (isPriceUp) {
        // 0.1% to 8.5% based on how much above median
        const ratio = Math.min(5.0, valueRatio);
        percentageChange = (0.1 + (ratio - 1) * 2).toFixed(2);
      } else {
        // -0.1% to -5.5% based on how much below median
        const ratio = Math.max(0.1, valueRatio);
        percentageChange = (0.1 + (1 - ratio) * 5).toFixed(2);
      }
    }
  } else {
    // For volume mode: Based on volume compared to median volume
    isPriceUp = item.totalVolume > medianVolume;
    valueRatio = item.totalVolume / medianVolume;
    
    if (isPriceUp) {
      // 0.1% to 8.5% based on how much above median
      const ratio = Math.min(5.0, valueRatio);
      percentageChange = (0.1 + (ratio - 1) * 2).toFixed(2);
    } else {
      // -0.1% to -5.5% based on how much below median
      const ratio = Math.max(0.1, valueRatio);
      percentageChange = (0.1 + (1 - ratio) * 5).toFixed(2);
    }
  }
  
  // Generate a hash-based small variation using the token ID
  // This adds a bit of variation without changing on each render
  const idSum = item.node.id.split('').reduce((sum, char) => sum + char.charCodeAt(0), 0);
  const smallVariation = (idSum % 50) / 100; // 0.00 to 0.49
  const finalPercentage = (parseFloat(percentageChange) + smallVariation).toFixed(2);

  // Calculate grid position first
  const row = Math.floor(index / gridCols);
  const col = index % gridCols;

  // Calculate center position - use let instead of const to allow reassignment for special positions
  let centerX = margin + col * cellWidth + cellWidth / 2;
  let centerY = margin + row * cellHeight + cellHeight / 2;
  
  // Remove cell size constraint to allow bubbles to be as large as intended
  // Don't limit by cell size - let bubbles overlap if needed
  const size = Math.max(minRadius, Math.min(maxRadius, scaledRadius));
  
  // Force dramatic size increases for the top bubbles with clear size steps
  let forcedSize;
  
  if (rank === 1) {
    // #1 token much larger in price mode for emphasis
    forcedSize = isPriceMode ? maxRadius : 60;
    // Position #1 token in the center of the viewport for maximum visibility
    centerX = dimensions.width / 2;
    centerY = dimensions.height / 2;
    console.log(`RANK #${rank} - FORCING LARGE SIZE: ${forcedSize}px - CENTER POSITION`);
  } else if (rank === 2) {
    // #2 token very large in price mode
    forcedSize = isPriceMode ? maxRadius * 0.85 : 55;
    // Position #2 token in the upper right quadrant
    centerX = dimensions.width * 0.75;
    centerY = dimensions.height * 0.35;
    console.log(`RANK #${rank} - FORCING LARGE SIZE: ${forcedSize}px - UPPER RIGHT`);
  } else if (rank === 3) {
    // #3 token large in price mode
    forcedSize = isPriceMode ? maxRadius * 0.75 : 50;
    // Position #3 token in the upper left quadrant
    centerX = dimensions.width * 0.25;
    centerY = dimensions.height * 0.35;
    console.log(`RANK #${rank} - FORCING LARGE SIZE: ${forcedSize}px - UPPER LEFT`);
  } else if (rank <= 5) {
    // #4-5 tokens medium-large in price mode
    forcedSize = isPriceMode ? maxRadius * 0.65 : 45;
    console.log(`RANK #${rank} - FORCING MEDIUM-LARGE SIZE: ${forcedSize}px`);
  } else if (rank <= 10) {
    // #6-10 tokens medium size in price mode
    forcedSize = isPriceMode ? maxRadius * 0.55 : 40;
    console.log(`RANK #${rank} - FORCING MEDIUM SIZE: ${forcedSize}px`);
  }
  
  if (forcedSize) {
    return {
      id: item.node.id,
      label: item.node.label || item.matchingToken?.label || 'Unknown',
      symbol: item.node.symbol || item.matchingToken?.symbol || '',
      icon: item.node.icon,
      x: centerX - forcedSize,
      y: centerY - forcedSize,
      size: forcedSize, // Force specific size based on rank
      rank,
      volume: item.totalVolume,
      price: item.price,
      normalizedVolume: normalizedVolumeValue,
      volumeRatio: item.totalVolume / medianVolume,
      formattedVolume,
      isPriceUp,
      percentageChange: finalPercentage,
      signedPercent: item.signedPercent,
      minVolume,
      maxVolume
    };
  }

  // For the rest of the bubbles - scale to ensure a reasonable minimum size.
  // In price mode, use minRadius as the floor so even the smallest bubbles are large enough
  // to read labels.
  const adjustedSize = Math.max(minRadius, Math.min(maxRadius, size));
  const x = centerX - adjustedSize;
  const y = centerY - adjustedSize;

  return {
    id: item.node.id,
    label: item.node.label || item.matchingToken?.label || 'Unknown',
    symbol: item.node.symbol || item.matchingToken?.symbol || '',
    icon: item.node.icon,
    x,
    y,
    size: adjustedSize, // Use the adjusted (doubled) size for regular bubbles
    rank,
    volume: item.totalVolume,
    price: item.price,
    normalizedVolume: normalizedVolumeValue, // Store the 0-100 normalized value
    volumeRatio: item.totalVolume / medianVolume, // Keep the median ratio for compatibility
    formattedVolume,
    isPriceUp,
    percentageChange: finalPercentage, // Store stable percentage change
    signedPercent: item.signedPercent,
    minVolume,
    maxVolume
  };
});

      // Log bubble size distribution for debugging
      const logSizeDistribution = (bubbles) => {
        // Get all sizes
        const sizes = bubbles.map(b => b.size);
        // Calculate size distribution
        const min = Math.min(...sizes);
        const max = Math.max(...sizes);
        const avg = sizes.reduce((sum, size) => sum + size, 0) / sizes.length;
        
        // Count bubbles in different size ranges
        const smallCount = sizes.filter(s => s < 50).length;
        const mediumCount = sizes.filter(s => s >= 50 && s < 150).length;
        const largeCount = sizes.filter(s => s >= 150).length;
        
        console.log(`Bubble size distribution: min=${min.toFixed(1)}px, max=${max.toFixed(1)}px, avg=${avg.toFixed(1)}px`);
        console.log(`Size counts: small=${smallCount}, medium=${mediumCount}, large=${largeCount}`);
      };
      
      logSizeDistribution(newBubbles);
      
      // Log the size of the top 5 and bottom 5 bubbles
      console.log("Top 5 bubbles by size:");
      newBubbles.slice(0, 5).forEach(b => 
        console.log(`${b.symbol}: ${Math.round(b.size)}px, volume=${b.formattedVolume}, normalized=${b.normalizedVolume}%`)
      );
      
      console.log("Bottom 5 bubbles by size:");
      newBubbles.slice(-5).forEach(b => 
        console.log(`${b.symbol}: ${Math.round(b.size)}px, volume=${b.formattedVolume}, normalized=${b.normalizedVolume}%`)
      );
      
      // COMPLETELY RANDOMIZE bubble positions to ensure even color distribution
      console.log("APPLYING TOTAL RANDOMIZATION FOR COLOR MIXING");
      
      // Separate green and red bubbles for counting
      const greenBubbles = newBubbles.filter(b => b.isPriceUp);
      const redBubbles = newBubbles.filter(b => !b.isPriceUp);
      console.log(`Original distribution: ${greenBubbles.length} green bubbles, ${redBubbles.length} red bubbles`);
      
      // Approach: Instead of trying to interleave arrays, we'll completely randomize
      // starting positions of bubbles in the viewport to break any patterns
      
      // Create a completely randomized copy of bubbles
      const randomizedBubbles = [...newBubbles];
      
      // Use the viewport dimensions already calculated above
      
      // Set minimum spacing between bubbles to avoid overlaps
      const minSpacing = 20;
      const safetyMargin = 40; // Keep away from edges
      
      // Function to get a random position in the viewport, ensuring we use the current window dimensions
      const getRandomPosition = (bubbleSize) => {
        // ALWAYS use current window dimensions directly, not cached values
        const currentWidth = window.innerWidth;
        const currentHeight = window.innerHeight;
        
        // Log the position calculation dimensions to verify we're using the right values
        console.log(`Generating bubble positions with dimensions: ${currentWidth}x${currentHeight}`);
        
        const diameter = bubbleSize * 2;
        const bottomMargin = 140; // Match animation bottom margin
        const effectiveHeight = currentHeight - bottomMargin; // keep away from bottom UI
        // Distribute across the width and effective height with safety margins and a bottom margin
        const x = safetyMargin + Math.random() * Math.max(10, (currentWidth - diameter - safetyMargin * 2));
        const maxY = effectiveHeight - diameter - safetyMargin;
        const y = safetyMargin + Math.random() * Math.max(10, maxY);
        
        // Sometimes log positions for debugging
        if (Math.random() < 0.05) { // Log ~5% of positions
          console.log(`Generated position: (${x.toFixed(0)}, ${y.toFixed(0)}) for bubble size ${bubbleSize}`);
        }
        
        return { x, y };
      };
      
      // Function to check if a position overlaps with existing bubbles
      const checkOverlap = (bubble, position, existingBubbles) => {
        const diameter = bubble.size * 2;
        for (const existing of existingBubbles) {
          const existingDiameter = existing.size * 2;
          const dx = position.x + diameter/2 - (existing.x + existingDiameter/2);
          const dy = position.y + diameter/2 - (existing.y + existingDiameter/2);
          const distance = Math.sqrt(dx*dx + dy*dy);
          
          if (distance < (diameter + existingDiameter)/2 + minSpacing) {
            return true; // Overlap detected
          }
        }
        return false; // No overlap
      };
      
      // Array to collect bubbles with random positions
      const randomizedPositionBubbles = [];
      
      // To ensure true mixing, alternate between red and green bubbles
      let redIndex = 0;
      let greenIndex = 0;
      
      // First, place the largest bubbles at specific positions
      const topBubbles = newBubbles.slice(0, 5);
      for (const bubble of topBubbles) {
        const position = getRandomPosition(bubble.size);
        randomizedPositionBubbles.push({
          ...bubble,
          x: position.x,
          y: position.y
        });
      }
      
      // Now alternate placing red and green bubbles
      const remainingBubbles = newBubbles.slice(5);
      const remainingRed = remainingBubbles.filter(b => !b.isPriceUp);
      const remainingGreen = remainingBubbles.filter(b => b.isPriceUp);
      
      // Shuffle both arrays for more randomness
      for (let i = remainingRed.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [remainingRed[i], remainingRed[j]] = [remainingRed[j], remainingRed[i]];
      }
      
      for (let i = remainingGreen.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [remainingGreen[i], remainingGreen[j]] = [remainingGreen[j], remainingGreen[i]];
      }
      
      // Interleave red and green bubbles with truly random positions
      const maxRemaining = Math.max(remainingRed.length, remainingGreen.length);
      for (let i = 0; i < maxRemaining; i++) {
        // Add green bubble if available
        if (i < remainingGreen.length) {
          const bubble = remainingGreen[i];
          const position = getRandomPosition(bubble.size);
          randomizedPositionBubbles.push({
            ...bubble,
            x: position.x,
            y: position.y
          });
        }
        
        // Add red bubble if available
        if (i < remainingRed.length) {
          const bubble = remainingRed[i];
          const position = getRandomPosition(bubble.size);
          randomizedPositionBubbles.push({
            ...bubble,
            x: position.x,
            y: position.y
          });
        }
      }
      
      // Verify our color mixing results
      const greenAfterRandomizing = randomizedPositionBubbles.filter(b => b.isPriceUp).length;
      const redAfterRandomizing = randomizedPositionBubbles.filter(b => !b.isPriceUp).length;
      console.log(`After randomizing: ${greenAfterRandomizing} green bubbles, ${redAfterRandomizing} red bubbles`);
      
      // Additional debug to verify mixing patterns
      let greenTop = 0;
      let greenBottom = 0;
      let redTop = 0;
      let redBottom = 0;
      
      // Consider top half vs bottom half of screen
      const midHeight = viewportHeight / 2;
      
      randomizedPositionBubbles.forEach(b => {
        const isTop = b.y < midHeight;
        if (b.isPriceUp) {
          isTop ? greenTop++ : greenBottom++;
        } else {
          isTop ? redTop++ : redBottom++;
        }
      });
      
      console.log(`Position distribution: TOP: ${greenTop} green, ${redTop} red | BOTTOM: ${greenBottom} green, ${redBottom} red`);
      
      // Reset animation counter and hide bubbles until they've moved
      frameCountRef.current = 0;
      setBubblesVisible(false);
      
      // Initialize physics properties for the bubbles with our randomly positioned array
      const physicsInitializedBubbles = initializePhysics(randomizedPositionBubbles);
      console.log(`Successfully created ${physicsInitializedBubbles.length} bubbles for animation`);
      setBubbles(physicsInitializedBubbles);
    } catch (error) {
      console.error('Error generating bubbles layout:', error);
      // Force loading to false so UI isn't stuck
      setLoading(false);
    } finally {
      setLoading(false);
      
      // Add a safety check to restart animation if it didn't start automatically
      setTimeout(() => {
        if (bubbles.length > 0 && !animationRef.current) {
          console.log('SAFETY: Restarting animation that failed to start automatically');
          animationRef.current = requestAnimationFrame(animate);
        }
      }, 500);
    }
  }, [tokenList, dimensions, graphData, sizeMode, priceSortMode]);

  const renderBubble = (bubble) => {
    // Determine if price is up or down using the signed percent from
    // the token's changes data (to match /price view semantics).
    const hasSignedPercent = typeof bubble.signedPercent === 'number';
    const isPriceUp = hasSignedPercent
      ? bubble.signedPercent >= 0
      : !!bubble.isPriceUp;
    
    // Determine ring color based on price direction - using brighter colors for better visibility
    const ringColor = isPriceUp ? '#00FF00' : '#FF0000'; // Brighter green/red
    const ringColorRgb = isPriceUp ? '0, 255, 0' : '255, 0, 0'; // RGB format for glow (full brightness)
    
    // Generate thin edge gradients with black center but brighter outer edge
    const baseColor = isPriceUp ? '0, 255, 0' : '255, 0, 0';
    const gradientStart = `rgba(${baseColor}, 1.0)`; // Full opacity for brighter edge
    const gradientMid = `rgba(${baseColor}, 0.7)`; // Increased brightness in mid-range
    const gradientEnd = `rgba(0, 0, 0, 1)`; // Keep black center for contrast
    
    // Enhanced glow effect - more vibrant and bright
    // This creates a more visually striking aura around each bubble
    const glowIntensity = 0.9; // Very high intensity glow
    // Make the glow extend about half as far as before
    const glowRadius = Math.max(6, bubble.size * 0.12); // About half the previous size
    const borderWidth = 1; // Thinner border as in screenshot
    
    // No pulse effect - keep bubbles at constant size
    const pulseScale = 1; // Fixed scale with no pulsating
    
    return (
      <div 
        key={bubble.id}
        style={{
          position: 'absolute',
          left: bubble.x,
          top: bubble.y,
          width: bubble.size * 2,
          height: bubble.size * 2,
          borderRadius: '50%',
          border: `${borderWidth}px solid ${ringColor}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          overflow: 'hidden',
          background: `radial-gradient(circle at 40% 40%, ${gradientStart}, ${gradientMid} 5%, ${gradientEnd} 20%)`,
          // Brighter outer glow and enhanced inner glow for more vibrant appearance
          boxShadow: `0 0 ${glowRadius}px ${glowRadius/3}px rgba(${ringColorRgb}, ${glowIntensity}), inset 0 0 7px rgba(${ringColorRgb}, 0.6)`,
          transform: `scale(${pulseScale})`,
          transition: 'transform 0.5s ease, box-shadow 0.5s ease',
          cursor: 'pointer',
          zIndex: Math.floor(10 + bubble.rank / 5) // Higher ranked bubbles show on top
        }}
        onClick={() => handleNodeClick(dataSource, graphData, null, bubble.id, false)}
      >
        {/* No rank badge in the screenshot */}
        {/* Display token icon with proper fallbacks */}
        <div style={{
          // Scale icon container size with bubble size - adjusted for much larger bubbles
          width: bubble.size > 500 ? '25%' : bubble.size > 400 ? '30%' : bubble.size > 200 ? '35%' : bubble.size > 100 ? '40%' : '35%',
          height: bubble.size > 500 ? '25%' : bubble.size > 400 ? '30%' : bubble.size > 200 ? '35%' : bubble.size > 100 ? '40%' : '35%',
          borderRadius: '50%',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'rgba(0,0,0,0.3)',
          boxShadow: 'inset 0 0 10px rgba(0,0,0,0.5)',
          marginBottom: bubble.size > 400 ? '20px' : bubble.size > 200 ? '15px' : bubble.size > 100 ? '10px' : bubble.size > 50 ? '5px' : '3px'
        }}>
          {/* Use PNG icons instead of SVG to avoid CORS issues */}
          {bubble.label && (
            <img 
              src={getBubbleIconSrc(bubble)} 
              alt={bubble.label}
              style={{ 
                // Scale icon size with bubble size for better visibility
                width: bubble.size > 60 ? '95%' : '90%', 
                height: bubble.size > 60 ? '95%' : '90%',
                objectFit: 'contain',
                display: 'block'
              }} 
              crossOrigin="anonymous"
              onLoad={(e) => {
                // Log successful icon loads for debugging
                if (bubble.rank <= 5) {
                  console.log(`✓ Icon loaded for ${bubble.label}:`, e.target.src);
                }
              }}
              onError={(e) => {
                const failedSrc = e && e.target && e.target.src ? String(e.target.src) : '';
                if (failedSrc) {
                  failedIconUrlsRef.current.add(failedSrc);
                }

                if (e && e.target && e.target.src !== genericTokenIcon) {
                  e.target.src = genericTokenIcon;
                  return;
                }

                if (e && e.target) {
                  console.log(`All icon sources failed for ${bubble.label}, using letter fallback`);
                  e.target.style.display = 'none';
                  
                  // Create and append letter element
                  const parent = e.target.parentNode;
                  if (!parent.querySelector('.token-letter')) {
                    const letterDiv = document.createElement('div');
                    letterDiv.className = 'token-letter';
                    letterDiv.style.width = '100%';
                    letterDiv.style.height = '100%';
                    letterDiv.style.display = 'flex';
                    letterDiv.style.alignItems = 'center';
                    letterDiv.style.justifyContent = 'center';
                    letterDiv.style.fontSize = bubble.size > 40 ? '24px' : '16px';
                    letterDiv.style.fontWeight = 'bold';
                    letterDiv.style.color = 'white';
                    letterDiv.textContent = (bubble.label || bubble.symbol || '?').charAt(0).toUpperCase();
                    parent.appendChild(letterDiv);
                  }
                }
              }}
            />
          )}
        </div>
        <div style={{ 
          color: 'white', 
          fontSize: bubble.size > 500 ? '100px' : bubble.size > 400 ? '80px' : bubble.size > 300 ? '64px' : bubble.size > 200 ? '48px' : bubble.size > 100 ? '32px' : bubble.size > 50 ? '24px' : '16px',
          textAlign: 'center',
          padding: '2px',
          width: '90%',
          fontWeight: 'bold',
          textShadow: '1px 1px 3px black',
          letterSpacing: '0.5px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          marginTop: bubble.size > 400 ? '16px' : bubble.size > 200 ? '10px' : bubble.size > 100 ? '6px' : bubble.size > 40 ? '2px' : '0px'
        }}>
          {bubble.symbol}
        </div>
        
        {/* We no longer need a secondary symbol display since we show only symbol as main text */}
        
        {/* Display percentage change with arrows, matching /price view */}
        {bubble.size >= 30 && typeof bubble.signedPercent === 'number' && isFinite(bubble.signedPercent) && (
          <div style={{ 
            color: 'rgba(255, 255, 255, 1)', 
            fontSize: bubble.size > 500 ? '64px' : bubble.size > 400 ? '48px' : bubble.size > 300 ? '36px' : bubble.size > 200 ? '28px' : bubble.size > 100 ? '20px' : bubble.size > 50 ? '16px' : '12px',
            fontWeight: 'bold',
            textAlign: 'center',
            width: '90%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            marginTop: '0px',
            letterSpacing: '0.5px'
          }}>
            {bubble.signedPercent >= 0 ? '▲' : '▼'} {Math.abs(bubble.signedPercent).toFixed(2)}%
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <div 
        ref={containerRef}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: '40px', // Leave space for the bottomContainer
          width: '100vw',
          height: 'calc(100vh - 40px)', // Adjust height for bottomContainer
          backgroundColor: 'black',
          overflow: 'hidden',
          zIndex: 1000 // Make sure it's above everything else
        }}
      >
   
      {/* Help icon with enhanced styling (price sort toggle moved to bottom bar) */}
      <div style={{ position: 'absolute', top: '10px', right: '10px', display: 'flex', gap: '8px', zIndex: 1001 }}>
        <div 
          onClick={() => setShowInfoPanel(!showInfoPanel)} 
          style={{
            backgroundColor: showInfoPanel ? 'rgba(74, 158, 255, 0.9)' : 'rgba(0, 0, 0, 0.7)',
            color: 'white',
            width: '34px',
            height: '34px',
            borderRadius: '50%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            cursor: 'pointer',
            boxShadow: showInfoPanel 
              ? '0 0 15px rgba(74, 158, 255, 0.7)' 
              : '0 0 10px rgba(0, 0, 0, 0.5)',
            border: showInfoPanel 
              ? '1px solid rgba(120, 180, 255, 0.8)' 
              : '1px solid rgba(100, 100, 100, 0.5)',
            transition: 'all 0.3s ease',
            transform: showInfoPanel ? 'scale(1.1)' : 'scale(1)',
          }}
          onMouseEnter={(e) => {
            if (!showInfoPanel) {
              e.currentTarget.style.transform = 'scale(1.1)';
              e.currentTarget.style.backgroundColor = 'rgba(50, 50, 80, 0.9)';
              e.currentTarget.style.boxShadow = '0 0 15px rgba(74, 158, 255, 0.5)';
            }
          }}
          onMouseLeave={(e) => {
            if (!showInfoPanel) {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.7)';
              e.currentTarget.style.boxShadow = '0 0 10px rgba(0, 0, 0, 0.5)';
            }
          }}
          title="View Price Change Visualization Guide"
        >
          <FaQuestionCircle size={18} style={{ color: showInfoPanel ? 'white' : '#4a9eff' }} />
        </div>
      </div>
      
      {/* Info panel that appears when the help icon is clicked */}
      {showInfoPanel && (
        <div style={{
          position: 'absolute',
          top: '50px',
          right: '10px',
          backgroundColor: 'rgba(0, 0, 0, 0.85)',
          color: 'white',
          padding: '15px 18px',
          borderRadius: '10px',
          fontSize: '14px',
          fontWeight: 'bold',
          zIndex: 999,
          boxShadow: '0 0 20px rgba(0, 0, 0, 0.7)',
          border: '1px solid rgba(80, 80, 80, 0.8)',
          maxWidth: '280px',
          backdropFilter: 'blur(8px)',
          ...fadeInAnimation,
          transition: 'all 0.3s ease',
          textAlign: 'left',
        }}>
          {/* Close button */}
          <div 
            style={{
              position: 'absolute',
              top: '10px',
              right: '10px',
              cursor: 'pointer',
              color: '#aaa',
              transition: 'color 0.2s ease',
            }}
            onClick={() => setShowInfoPanel(false)}
            onMouseEnter={(e) => e.currentTarget.style.color = 'white'}
            onMouseLeave={(e) => e.currentTarget.style.color = '#aaa'}
          >
            <FaTimes size={14} />
          </div>
          
          <div style={{ 
            fontSize: '16px', 
            marginBottom: '10px', 
            borderBottom: '1px solid #444', 
            paddingBottom: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            textAlign: 'left'
          }}>
            <FaQuestionCircle size={14} style={{ color: '#4a9eff' }} />
            <span>Token Price Change Visualization</span>
          </div>
          
          <div style={{ fontSize: '12px', color: '#aaa', lineHeight: '1.6', marginTop: '8px', textAlign: 'left' }}>
            {sizeMode === 'price' ? (
              <>
                <p>• <strong>Bubble size</strong> represents magnitude of price change (larger = bigger % change)</p>
                <p>• <strong>Green rings</strong> show tokens with price increases</p>
                <p>• <strong>Red rings</strong> show tokens with price decreases</p>
                <p>• <strong>Percentages</strong> show actual price change over the selected time period</p>
                <p>• Choose time intervals (30M to 24H) to compare current prices with historical values</p>
                <p>• Bubbles are sized directly proportional to percentage change magnitude</p>
                <p>• Visually identify tokens with significant price movements at a glance</p>
              </>
            ) : (
              <>
                <p>• <strong>Bubble size</strong> represents token trading volume (larger = higher volume)</p>
                <p>• Sizes are normalized between 20px and 180px with top tokens given prominence</p>
                <p>• <strong>Green rings</strong> show tokens with volume above median</p>
                <p>• <strong>Red rings</strong> show tokens with volume below median</p>
                <p>• <strong>Percentages</strong> indicate how far from median volume (±0.1% to ±8.5%)</p>
                <p>• Higher ranked tokens appear above lower ranked ones</p>
              </>
            )}
          </div>
        </div>
      )}
      
      {!isDataReady || !hasPriceHistoryData || !tokenList || tokenList.length === 0 ? (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          color: 'white',
          fontSize: '24px',
          textAlign: 'center',
          zIndex: 1100
        }}>
          <div style={{ marginBottom: '20px' }}>
            {!hasPriceHistoryData ? 'Loading Price History Data...' : !isDataReady ? 'Loading...' : 'Preparing Display...'}
          </div>
          <div
            style={{
              display: 'inline-block',
              width: '50px',
              height: '50px',
              border: '5px solid rgba(255,255,255,0.3)',
              borderRadius: '50%',
              borderTopColor: 'white',
              animation: 'spin 1s ease-in-out infinite',
            }}
          />
          <style>{`
            @keyframes spin {
              to { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      ) : loading ? (
        <div style={{
          color: 'white',
          fontSize: '18px',
          textAlign: 'center',
          paddingTop: '20px'
        }}>
          Loading token data...
        </div>
      ) : bubbles.length === 0 ? (
        <div style={{
          color: 'white',
          fontSize: '18px',
          textAlign: 'center',
          paddingTop: '20px',
          maxWidth: '500px',
          margin: '0 auto'
        }}>
          <div style={{ marginBottom: '10px' }}>
            No nodes found with icons and matching token volumes
          </div>
          <div style={{ fontSize: '14px', color: '#aaa' }}>
            The bubbles view only shows nodes from graphData that have icons and matching tokens in tokenList with volume data.
          </div>
        </div>
      ) : !bubblesVisible ? (
        // Don't show anything until bubbles have moved from their initial positions
        <div style={{
          width: '100%',
          height: '100%',
          position: 'relative'
        }}></div>
      ) : (
        bubbles.map(bubble => renderBubble(bubble))
      )}
      </div>

      {/* Bottom bar with controls and stats (decoupled from bubble animation) */}
      <BubblesBottomBar
        isMobile={isMobile}
        handleMenuClick={handleMenuClick}
        handleIntervalSelection={handleIntervalSelection}
        rangeSelected={rangeSelected}
        bubbleDensity={bubbleDensity}
        setBubbleDensity={setBubbleDensity}
        tokenCount={bubbles.length}
        classes={classes}
        sizeMode={sizeMode}
        priceSortMode={priceSortMode}
        setPriceSortMode={setPriceSortMode}
        hasAdBanner={hasAdBanner}
      />
    </>
  );
};

export default BubblesView;
